import React, { useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Buildings, PencilSimple, TrashSimple, Calendar, User, Briefcase, Stack, ChartLine, FileText } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import CityDistrictPicker from "@/components/CityDistrictPicker";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/dateUtils";
import { titleCaseTR, toUpperTR, searchMatchTR } from "@/lib/textFormat";
import ProjectInfoDialog from "@/components/ProjectInfoDialog";
import { Link } from "react-router-dom";

const emptyForm = {
  code: "", name: "", address: "", city: "", district: "",
  project_manager: "", project_manager_phone: "",
  start_date: "", end_date: "", budget: "",
  status: "aktif", notes: "",
};

export default function Sites() {
  const { user } = useAuth();
  const { sites, reloadSites } = useSites();
  const askConfirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc|name_desc|code_asc|start_desc|start_asc|status
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [projectSite, setProjectSite] = useState(null); // Aktif proje dialog için şantiye
  const canManage = can.manageSite(user);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const visible = useMemo(() => {
    let out = sites;
    if (q && q.trim()) {
      out = sites.filter(s => searchMatchTR(q, [
        s.code, s.name, s.city, s.district, s.address,
        s.project_manager, s.project_manager_phone, s.notes,
      ].filter(Boolean).join(" ")));
    }
    const cmp = (a, b) => (a.name || "").localeCompare(b.name || "", "tr", {
      numeric: true, sensitivity: "base",
    });
    const sorted = [...out];
    if (sortBy === "name_asc") sorted.sort(cmp);
    else if (sortBy === "name_desc") sorted.sort((a, b) => cmp(b, a));
    else if (sortBy === "code_asc") sorted.sort((a, b) =>
      (a.code || "").localeCompare(b.code || "", "tr", { numeric: true, sensitivity: "base" }));
    else if (sortBy === "start_desc") sorted.sort((a, b) =>
      (b.start_date || "").localeCompare(a.start_date || "") || cmp(a, b));
    else if (sortBy === "start_asc") sorted.sort((a, b) =>
      (a.start_date || "").localeCompare(b.start_date || "") || cmp(a, b));
    else if (sortBy === "status") sorted.sort((a, b) => {
      // aktif önce, sonra kapalı
      const av = (a.status === "aktif") ? 0 : 1;
      const bv = (b.status === "aktif") ? 0 : 1;
      return av - bv || cmp(a, b);
    });
    return sorted;
  }, [sites, q, sortBy]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      code: s.code || "", name: s.name || "", address: s.address || "",
      city: s.city || "", district: s.district || "",
      project_manager: s.project_manager || "", project_manager_phone: s.project_manager_phone || "",
      start_date: s.start_date || "", end_date: s.end_date || "",
      budget: s.budget || "", status: s.status || "aktif", notes: s.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code || !form.name) { toast.error("Kod ve ad zorunlu"); return; }
    try {
      const payload = {
        ...form,
        code: toUpperTR(form.code),
        name: titleCaseTR(form.name),
        project_manager: titleCaseTR(form.project_manager),
        address: titleCaseTR(form.address),
        notes: titleCaseTR(form.notes),
        budget: parseFloat(form.budget) || 0,
      };
      if (editing) await axios.put(`${API}/sites/${editing.id}`, payload);
      else await axios.post(`${API}/sites`, payload);
      toast.success(editing ? "Şantiye güncellendi" : "Şantiye eklendi");
      setOpen(false);
      reloadSites();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (s) => {
    const ok = await askConfirm({
      title: "Şantiyeyi Sil",
      description: `"${s.name}" şantiyesini silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/sites/${s.id}`);
      toast.success("Silindi");
      reloadSites();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectAllVisible = (rows) => setSelectedIds(new Set(rows.map(r => r.id)));

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Şantiyeleri Sil",
      description: `${selectedIds.size} şantiyeyi silmek istiyor musunuz? Aktif malzeme kaydı olan şantiyeler atlanır.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const r = await axios.post(`${API}/sites/bulk-delete`, { ids: Array.from(selectedIds) });
      const skipped = r.data?.skipped || [];
      if (r.data?.deleted > 0) toast.success(`${r.data.deleted} şantiye silindi`);
      if (skipped.length > 0) {
        toast.warning(`${skipped.length} şantiye atlandı: ${skipped.map(s => s.name).join(", ")}`);
      }
      clearSelection();
      reloadSites();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBulkDeleting(false); }
  };

  return (
    <Shell
      title="Proje Künyesi"
      hideSiteSelector
      actions={canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-site"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <Plus size={16} className="mr-2" /> Yeni Şantiye
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:rounded-sm max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-bold tracking-tight font-heading">
                {editing ? "Şantiye Düzenle" : "Yeni Şantiye"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye Kodu</Label>
                <Input value={form.code} data-testid="site-code"
                       onChange={e => set("code", toUpperTR(e.target.value))}
                       placeholder="Örn: MSS-01"
                       className="mt-2 rounded-none sm:rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Durum</Label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="site-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="kapali">Kapalı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Şantiye Adı</Label>
                <Input value={form.name} data-testid="site-name"
                       onChange={e => set("name", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Proje Müdürü</Label>
                <Input value={form.project_manager}
                       onChange={e => set("project_manager", titleCaseTR(e.target.value))}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Müdür Telefonu</Label>
                <Input value={form.project_manager_phone}
                       onChange={e => set("project_manager_phone", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Başlangıç Tarihi</Label>
                <Input type="date" value={form.start_date}
                       onChange={e => set("start_date", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Bitiş Tarihi</Label>
                <Input type="date" value={form.end_date}
                       onChange={e => set("end_date", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Sözleşme Bedeli (₺)</Label>
                <Input type="number" value={form.budget} data-testid="site-budget"
                       onChange={e => set("budget", e.target.value)}
                       className="mt-2 rounded-none sm:rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Adres</Label>
                <Textarea value={form.address} onChange={e => set("address", titleCaseTR(e.target.value))}
                          className="mt-2 rounded-none sm:rounded-sm" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">İl / İlçe</Label>
                <div className="mt-2">
                  <CityDistrictPicker
                    city={form.city}
                    district={form.district}
                    onChange={(v) => setForm(f => ({ ...f, city: v.city, district: v.district }))}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Notlar</Label>
                <Textarea value={form.notes} onChange={e => set("notes", titleCaseTR(e.target.value))}
                          className="mt-2 rounded-none sm:rounded-sm" rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} data-testid="save-site"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                {editing ? "Güncelle" : "Ekle"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    >
      {/* Arama + Sıralama */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Kod, şantiye adı, şehir, ilçe, yönetici…"
          data-testid="site-search"
          className="rounded-none sm:rounded-sm h-9 max-w-md"
        />
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="rounded-none sm:rounded-sm h-9 w-[200px]" data-testid="site-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name_asc">Ada Göre (A → Z)</SelectItem>
            <SelectItem value="name_desc">Ada Göre (Z → A)</SelectItem>
            <SelectItem value="code_asc">Koda Göre</SelectItem>
            <SelectItem value="start_desc">Başlangıç (Yeni → Eski)</SelectItem>
            <SelectItem value="start_asc">Başlangıç (Eski → Yeni)</SelectItem>
            <SelectItem value="status">Duruma Göre (Aktif önce)</SelectItem>
          </SelectContent>
        </Select>
        {q && (
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 whitespace-nowrap">
            {visible.length} / {sites.length}
          </span>
        )}
      </div>

      {/* Toplu Seçim Bar'ı */}
      {canManage && selectedIds.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-[#1B4776] text-white rounded-none sm:rounded-sm"
             data-testid="site-bulk-bar">
          <span className="text-xs uppercase tracking-widest font-bold">
            {selectedIds.size} şantiye seçildi
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection}
                    data-testid="site-bulk-clear"
                    className="rounded-none sm:rounded-sm text-xs uppercase tracking-widest font-bold bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white">
              Seçimi Temizle
            </Button>
            <Button size="sm" onClick={bulkDelete} disabled={bulkDeleting}
                    data-testid="site-bulk-delete"
                    className="rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-widest font-bold">
              <TrashSimple size={14} className="mr-1.5" />
              {bulkDeleting ? "Siliniyor..." : "Seçilenleri Sil"}
            </Button>
          </div>
        </div>
      )}

      {/* Tümünü Seç butonu */}
      {canManage && visible.length > 0 && (
        <div className="mb-3 flex items-center gap-3 text-[10px] uppercase tracking-widest font-bold text-slate-500">
          <button
            data-testid="site-select-all"
            onClick={() => {
              const allSelected = visible.every(r => selectedIds.has(r.id));
              if (allSelected) clearSelection();
              else selectAllVisible(visible);
            }}
            className="hover:text-[#1B4776]"
          >
            {visible.every(r => selectedIds.has(r.id)) && visible.length > 0
              ? "Seçimi Kaldır"
              : "Tümünü Seç"}
          </button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((s) => (
          <Card key={s.id} data-testid={`site-card-${s.id}`}
                className={`p-6 rounded-none sm:rounded-sm border shadow-sm bg-white ${
                  s.status === "kapali" ? "opacity-60" : ""
                } ${selectedIds.has(s.id) ? "border-[#1B4776] ring-2 ring-[#1B4776]/30" : "border-slate-200"}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-2">
                {canManage && (
                  <Checkbox
                    checked={selectedIds.has(s.id)}
                    onCheckedChange={() => toggleOne(s.id)}
                    data-testid={`site-check-${s.id}`}
                    className="mt-2"
                  />
                )}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-[#1B4776] flex items-center justify-center">
                    <Buildings size={22} weight="duotone" className="text-[#95C11F]" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 font-mono">
                      {s.code}
                    </div>
                    <div className="text-lg font-bold text-slate-900 font-heading leading-tight">{s.name}</div>
                  </div>
                </div>
              </div>
              {canManage && (
                <div className="flex gap-1">
                  <button onClick={() => setProjectSite(s)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-[#F7FBE9]"
                          data-testid={`project-info-${s.id}`}
                          title="Proje Bilgileri">
                    <Briefcase size={14} weight="duotone" className="text-[#95C11F]" />
                  </button>
                  <Link to={`/proje/${s.id}/yapisi`}
                        className="w-8 h-8 flex items-center justify-center hover:bg-[#EFF4F9]"
                        data-testid={`project-structure-${s.id}`}
                        title="Etap & Blok Yapısı">
                    <Stack size={14} weight="duotone" className="text-[#1B4776]" />
                  </Link>
                  <Link to={`/proje/${s.id}/gantt`}
                        className="w-8 h-8 flex items-center justify-center hover:bg-[#F0F7EE]"
                        data-testid={`project-gantt-${s.id}`}
                        title="Gantt Şeması">
                    <ChartLine size={14} weight="duotone" className="text-[#95C11F]" />
                  </Link>
                  <Link to={`/proje/${s.id}/gun-sonu`}
                        className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                        data-testid={`project-daily-report-${s.id}`}
                        title="Gün Sonu Raporu">
                    <FileText size={14} weight="duotone" className="text-slate-700" />
                  </Link>
                  <button onClick={() => openEdit(s)} className="w-8 h-8 flex items-center justify-center hover:bg-slate-100"
                          data-testid={`edit-site-${s.id}`}>
                    <PencilSimple size={14} className="text-slate-600" />
                  </button>
                  <button onClick={() => del(s)} className="w-8 h-8 flex items-center justify-center hover:bg-red-50"
                          data-testid={`del-site-${s.id}`}>
                    <TrashSimple size={14} className="text-red-600" />
                  </button>
                </div>
              )}
            </div>

            <div className="mt-4 space-y-2 text-sm">
              {s.project_manager && (
                <div className="flex items-center gap-2 text-slate-700">
                  <User size={14} className="text-slate-400" />
                  <span>{s.project_manager}</span>
                  {s.project_manager_phone && (
                    <span className="text-slate-500 text-xs font-mono">· {s.project_manager_phone}</span>
                  )}
                </div>
              )}
              {(s.start_date || s.end_date) && (
                <div className="flex items-center gap-2 text-slate-600 text-xs">
                  <Calendar size={14} className="text-slate-400" />
                  <span className="font-mono">{fmtDate(s.start_date) || "?"} → {fmtDate(s.end_date) || "?"}</span>
                </div>
              )}
              {(s.city || s.district || s.address) && (
                <div className="text-xs text-slate-600 mt-2">
                  {(s.city || s.district) && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Konum</span>
                      <span className="font-medium">
                        {[s.district, s.city].filter(Boolean).join(" / ")}
                      </span>
                    </div>
                  )}
                  {s.address && (
                    <div className="text-slate-500 line-clamp-2">{s.address}</div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 ${
                s.status === "aktif"
                  ? "bg-[#95C11F] text-[#1B2A3A]"
                  : "bg-slate-300 text-slate-700"
              }`}>
                {s.status === "aktif" ? "Aktif" : "Kapalı"}
              </span>
              {s.budget > 0 && (
                <span className="text-xs font-mono text-slate-600">
                  ₺{s.budget.toLocaleString("tr-TR")}
                </span>
              )}
            </div>

            {s.project && (
              <div className="mt-3 pt-3 border-t border-dashed border-[#95C11F]/40 space-y-1"
                   data-testid={`project-summary-${s.id}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-[#1B4776]">
                  <Briefcase size={11} weight="duotone" className="text-[#95C11F]" />
                  Proje Bilgileri
                </div>
                {s.project.employer_name && (
                  <div className="text-xs text-slate-700">
                    <span className="text-slate-500">İşveren:</span> <span className="font-medium">{s.project.employer_name}</span>
                  </div>
                )}
                {(s.project.project_type_label || s.project.project_subtype_label) && (
                  <div className="text-xs text-slate-700">
                    <span className="text-slate-500">Tip:</span>{" "}
                    <span className="font-medium">
                      {[s.project.project_type_label, s.project.project_subtype_label].filter(Boolean).join(" › ")}
                    </span>
                  </div>
                )}
                {s.project.contract_amount > 0 && (
                  <div className="text-xs text-slate-700">
                    <span className="text-slate-500">Bedel:</span>{" "}
                    <span className="font-mono font-medium">
                      {s.project.contract_currency === "USD" ? "$"
                        : s.project.contract_currency === "EUR" ? "€" : "₺"}
                      {(s.project.contract_amount || 0).toLocaleString("tr-TR")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
        {visible.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500" data-testid="no-sites">
            {q && sites.length > 0
              ? `"${q}" için sonuç bulunamadı.`
              : canManage ? "Henüz şantiye yok. Sağ üstten yeni şantiye ekleyin." : "Size atanmış şantiye yok."}
          </div>
        )}
      </div>

      {/* Proje Bilgileri Dialog */}
      <ProjectInfoDialog
        site={projectSite}
        onClose={() => setProjectSite(null)}
        onSaved={() => reloadSites()}
      />
    </Shell>
  );
}
